import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, CalendarClock, BarChart3, Swords, Lightbulb, LayoutGrid, Upload, Link2, Play, Puzzle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AddonDialog } from "@/components/social/AddonDialog";
import { useSuperRawaj } from "@/components/social/SuperRawajPanel";

const FEATURES = [
  { icon: CalendarClock, t: "نشر تلقائي بأفضل الأوقات", d: "كل منشور معتمد يُنشر في موعده بأفضل وقت وصول لكل منصة — بلا رفع يدوي." },
  { icon: BarChart3, t: "متابعة الأداء تلقائياً", d: "الإعجابات والمشاهدات والتعليقات تُسحب وحدها وتظهر في رسوم يوم/أسبوع/شهر/سنة." },
  { icon: LayoutGrid, t: "لوحة قنواتك", d: "إنستغرام متاح الآن، وتيك توك وسناب وإكس تُضاف لاحقاً وتظهر تلقائياً — متابعوك ونموهم في مكان واحد." },
  { icon: Swords, t: "مراقبة المنافسين", d: "أضف منافسيك أو دع الذكاء الاصطناعي يقترحهم، وقارن حضورك بهم." },
  { icon: Lightbulb, t: "توصيات ومركزك في السوق", d: "ملخص تنفيذي بالذكاء الاصطناعي: أين تقف وما الخطوة التالية." },
];

// شريط مدمج أعلى مرحلة التنفيذ: زر Add-on يفتح المزايا + أيقونة فيديو طولية صغيرة لمن يرغب بالمشاهدة
export const SuperRawajIntro = () => {
  const navigate = useNavigate();
  const { addon, reload } = useSuperRawaj();
  const [promo, setPromo] = useState(null);
  const [features, setFeatures] = useState(false);
  const [activate, setActivate] = useState(false);

  useEffect(() => {
    api.get("/addons/super_rawaj/promo").then((r) => {
      const u = r.data.url || null;
      setPromo(u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
    }).catch(() => {});
  }, []);
  if (!addon) return null;

  const cta = addon.active
    ? <Button onClick={() => { setFeatures(false); navigate("/app/social"); }} className="gap-1 font-bold bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="super-rawaj-intro-connect"><Link2 className="w-4 h-4" /> اربط حساباتك وافتح لوحة القنوات</Button>
    : <Button onClick={() => { setFeatures(false); setActivate(true); }} className="gap-1 font-bold bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="super-rawaj-intro-activate"><Zap className="w-4 h-4" /> فعّل سوبر رواج — {addon.price} ريال/شهر</Button>;

  return (
    <div className="mb-2 space-y-3" data-testid="super-rawaj-intro">
    <div className="flex items-stretch gap-3">
      <button onClick={() => setFeatures(true)} data-testid="super-rawaj-addon-btn"
        className="flex-1 glass rounded-2xl border border-accent/30 hover:border-accent/60 transition-colors px-4 py-3 flex items-center justify-between gap-3 text-start">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-accent/15 text-accent flex items-center justify-center shrink-0"><Puzzle className="w-5 h-5" /></span>
          <div>
            <div className="flex items-center gap-2 font-extrabold text-sm">
              <span className="notranslate" translate="no">سوبر رواج</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent/15 text-accent"><span className="whitespace-nowrap">Add-on</span></span>
              {addon.active && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> مفعّل</span>}
            </div>
            <div className="text-xs text-muted-foreground">نشر تلقائي ومتابعة أداء ولوحة قنوات ومنافسين — اضغط للتعرف على المزايا{promo ? " ومشاهدة الفيديو" : ""}{addon.active ? "" : ` · ${addon.price} ريال/شهر`}</div>
          </div>
        </div>
        <span className="text-xs text-accent font-bold shrink-0 flex items-center gap-1">{promo && <Play className="w-3.5 h-3.5" />} المزايا ←</span>
      </button>
    </div>
      {features && (
        <Dialog open onOpenChange={(o) => !o && setFeatures(false)}>
          <DialogContent className="bg-popover border-border max-w-2xl w-[96vw] max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="super-rawaj-features-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-accent"><Zap className="w-5 h-5" /> سوبر رواج — خلّ حملتك تشتغل وحدها</DialogTitle>
              <DialogDescription>بعد توليد محتوى حملتك واعتماده، اختر: ينشر سوبر رواج ويتابع الأداء عنك، أو تكمل يدوياً بالرفع وإدخال الأرقام بنفسك.</DialogDescription>
            </DialogHeader>
            {promo && (
              <div className="rounded-2xl overflow-hidden border border-accent/30 bg-black" data-testid="super-rawaj-promo-wrap">
                <video src={promo} controls playsInline preload="metadata" className="w-full aspect-video" data-testid="super-rawaj-promo-video" />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-2">
              {FEATURES.map(({ icon: Icon, t, d }) => (
                <div key={t} className="rounded-xl border border-border bg-background/40 p-3">
                  <div className="flex items-center gap-2 text-sm font-bold"><Icon className="w-4 h-4 text-primary" /> {t}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{d}</div>
                </div>
              ))}
              <div className="rounded-xl border border-dashed border-border p-3">
                <div className="flex items-center gap-2 text-sm font-bold text-muted-foreground"><Upload className="w-4 h-4" /> أو المسار اليدوي</div>
                <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">حمّل المحتوى وانشره بنفسك ثم أدخل أرقام الأداء يدوياً — بلا ربط حسابات.</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 items-center pt-2">
              {cta}
              <Button variant="ghost" onClick={() => setFeatures(false)} data-testid="super-rawaj-intro-manual">أكمل يدوياً</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {activate && <AddonDialog addon={addon} onClose={() => setActivate(false)} onActivated={reload} />}
    </div>
  );
};
